import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from 'lucide-react'
import { action, makeObservable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import TwoMonthsDatePickerStore, {
  TwoMonthsDatePickerMode,
  getTwoMonthsDatePickerModeDisplayName,
} from '~/client/src/desktop/components/TwoMonthsDatePicker/TwoMonthsDatePicker.store'
import FilterHandleButton from '~/client/src/shared/components/Filters/components/FilterHandleButton/FilterHandleButton'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import DateSelectorStore, { IDateFilters } from './DateSelector.store'
import GeneralDatePicker from './GeneralDatePicker/GeneralDatePicker'

// localization: translated

interface IDateSelectorProps {
  filters: IDateFilters
  projectsStore: ProjectsStore
  projectDateStore: ProjectDateStore
  mode: TwoMonthsDatePickerMode
  shouldRenderDateModes?: boolean
  datePickerModes?: TwoMonthsDatePickerMode[]
  onShowChanged?: (isShown: boolean) => void
  forceClose?: boolean
  shouldEnableFullProjectMode?: boolean
  shouldUseOtherDatesForFullProjectMode?: boolean
  shouldUseRelativePosition?: boolean
  shouldShowTodayButton?: boolean
  onOptionModeClick?: (mode: TwoMonthsDatePickerMode) => void
  shouldHideTopButtons?: boolean
  todayStyle?: string
  isAlternativeArrows?: boolean
  isAlternativeButton?: boolean

  customDatePickerViewRenderer?: (
    store: DateSelectorStore,
    datePickerStore: TwoMonthsDatePickerStore,
  ) => JSX.Element
  onDateChange?: (startDate: number, endDate: number) => void
  getCustomModeDisplayName?: (mode: TwoMonthsDatePickerMode) => string
}

const ICON_SIZE = 18

@observer
export default class DateSelector<
  T extends IDateSelectorProps,
> extends React.Component<T> {
  public static defaultProps = {
    shouldUseRelativePosition: true,
  }

  protected readonly store: DateSelectorStore
  protected readonly datePickerStore: TwoMonthsDatePickerStore

  public constructor(props: T) {
    super(props)

    this.datePickerStore = new TwoMonthsDatePickerStore(
      props.projectDateStore,
      props.onShowChanged,
    )
    this.store = new DateSelectorStore(
      this.datePickerStore,
      props.filters,
      props.projectsStore,
      props.projectDateStore,
      props.shouldUseOtherDatesForFullProjectMode,
      props.onDateChange,
    )
    makeObservable(this)
  }

  public componentDidMount() {
    this.store.setInitialMode(this.props.mode)
  }

  @action
  public componentDidUpdate(prevProps: IDateSelectorProps) {
    if (this.props.forceClose) {
      this.datePickerStore.isShown = false
    }

    if (this.props.mode !== prevProps.mode) {
      this.store.setInitialMode(this.props.mode)
    }
  }

  public render() {
    const {
      shouldEnableFullProjectMode,
      shouldUseOtherDatesForFullProjectMode,
      shouldRenderDateModes,
      shouldShowTodayButton,
      shouldUseRelativePosition,
      shouldHideTopButtons,
    } = this.props

    return (
      <div
        className={classList({
          'row calendar-control x-end': true,
          relative: shouldUseRelativePosition,
        })}
      >
        {shouldShowTodayButton && this.renderTodayButton()}
        {this.renderDatePickerView()}
        {shouldRenderDateModes && this.renderDatePickerStates()}
        <GeneralDatePicker
          datePickerStore={this.datePickerStore}
          shouldEnableFullProjectMode={shouldEnableFullProjectMode}
          shouldUseOtherDatesForFullProjectMode={
            shouldUseOtherDatesForFullProjectMode
          }
          shouldHideTopButtons={shouldHideTopButtons}
        />
      </div>
    )
  }

  protected renderDatePickerView() {
    if (this.props.customDatePickerViewRenderer) {
      return this.props.customDatePickerViewRenderer(
        this.store,
        this.datePickerStore,
      )
    }

    const { filters, isAlternativeArrows, isAlternativeButton } = this.props
    const { isDateFilterActive } = filters
    const { isShown } = this.datePickerStore
    const {
      isCurrentActive,
      canChangeDates,
      datesButtonCaption,
      datesFullButtonCaption,
      moveDatesBack,
      moveDatesForward,
      toggleDatePicker,
    } = this.store

    return (
      <>
        {!isAlternativeArrows && (
          <div className="no-grow pl15 pr5">
            {canChangeDates && (
              <span
                className="gant-left-navigation pointer left-navigation tw:flex"
                onClick={moveDatesBack}
              >
                <ChevronLeftIcon size={20} />
              </span>
            )}
          </div>
        )}

        <FilterHandleButton
          className={classList({
            'row no-grow date-handle-button text grey-light large': true,
            mw180: isAlternativeButton,
            'zone-map-view-calendar default-cursor': !canChangeDates,
            'ba-palette-grey': !isCurrentActive,
            'current-lookahead ba-palette-secondary-orange secondary-orange':
              isCurrentActive,
          })}
          onClick={toggleDatePicker}
          isSelected={isShown}
          isActive={isDateFilterActive}
          hasIndent={false}
        >
          <span>
            {isAlternativeButton ? datesFullButtonCaption : datesButtonCaption}
          </span>
          {isAlternativeButton && (
            <span className="pointer right-navigation pa0 tw:flex-none!">
              <ChevronDownIcon size={ICON_SIZE} />
            </span>
          )}
        </FilterHandleButton>

        {!isAlternativeArrows && (
          <div className="no-grow pl5 pr15">
            {canChangeDates && (
              <span
                className="pointer right-navigation"
                onClick={moveDatesForward}
              >
                <ChevronRightIcon size={20} />
              </span>
            )}
          </div>
        )}
        {isAlternativeArrows && (
          <div className="row no-grow">
            <span
              className="ma6 gant-left-navigation pointer left-navigation tw:inline-flex tw:flex-none!"
              onClick={moveDatesBack}
            >
              <ChevronLeftIcon size={ICON_SIZE} />
            </span>
            <span
              className="ma6 pointer right-navigation tw:flex-none!"
              onClick={moveDatesForward}
            >
              <ChevronRightIcon size={ICON_SIZE} />
            </span>
          </div>
        )}
      </>
    )
  }

  private renderTodayButton() {
    return (
      <div
        className={classList({
          'row x-center y-center btn-filter gant-header-btn no-flex gant-header-btn-outline ml15 mr5': true,
          [this.props.todayStyle]: true,
        })}
        onClick={this.store.setDateToToday}
      >
        <span>{Localization.translator.today}</span>
      </div>
    )
  }

  private getDatePickerIcon = (): JSX.Element => {
    const ToggleIcon = this.store.shouldShowDatePickerOptions
      ? ChevronUpIcon
      : ChevronDownIcon
    return <ToggleIcon size={ICON_SIZE} className="mx5" />
  }

  private renderDatePickerStates() {
    const {
      datePickerModes,
      shouldUseRelativePosition,
      isAlternativeButton,
      getCustomModeDisplayName,
    } = this.props
    const {
      shouldShowDatePickerOptions,
      datePickerStates,
      datePickerMode,
      toggleDatePickerModesList,
    } = this.store

    const datePickerOptions = datePickerModes || datePickerStates

    return (
      <div
        className={classList({
          'no-grow ml5': true,
          relative: shouldUseRelativePosition,
        })}
      >
        {shouldShowDatePickerOptions && (
          <div
            className={classList({
              'date-picker-options-holder absolute ba-palette-grey bg-white brada4': true,
              mw100: isAlternativeButton,
              mw120: !isAlternativeButton,
            })}
          >
            {datePickerOptions.map((mode, index) => {
              return (
                <span
                  key={index}
                  className={classList({
                    'pointer row x-center y-center text large bg-white option': true,
                    active: datePickerMode === mode,
                  })}
                  onClick={this.onPickerModeClick.bind(this, mode)}
                >
                  {getCustomModeDisplayName?.(mode) ||
                    getTwoMonthsDatePickerModeDisplayName(mode)}
                </span>
              )
            })}
          </div>
        )}
        <div
          className={classList({
            'large pl10 text capitalize relative row x-center y-center gant-header-btn no-flex gant-header-btn-outline ba-palette-grey text-ellipsis': true,
            mw100: isAlternativeButton,
            mw120: !isAlternativeButton,
          })}
          onClick={toggleDatePickerModesList}
        >
          {getCustomModeDisplayName?.(datePickerMode) ||
            getTwoMonthsDatePickerModeDisplayName(datePickerMode)}
          {this.getDatePickerIcon()}
        </div>
      </div>
    )
  }

  private onPickerModeClick = (
    mode: TwoMonthsDatePickerMode,
    event: React.MouseEvent,
  ) => {
    if (event) {
      event.stopPropagation()
    }

    this.store.setDatePickerMode(mode)

    const { onOptionModeClick } = this.props

    if (onOptionModeClick) {
      onOptionModeClick(mode)
    }
  }
}
